import { useState, useEffect } from 'react';
import { NotificationToast } from '../../../../../components/organisms/notification-toast';
import Modal, {
	ModalProps,
} from '../../../../../components/template/mvc-modal';
import Toggle from '../../../../../components/molecules/toggle';
import { useTranslation } from 'react-i18next';
import { EditableChangeEvent } from '../../../../../utils/types/editable';
import Input from '../../../../../components/atoms/input';
import YardService, { IYard } from '../../../../../services/Yard.service';
import { IHarbor } from '../../../../../services/Harbor.service';

export interface YardsModalProps extends ModalProps<IYard> {
	harbor: IHarbor;
	setModalProps: React.Dispatch<React.SetStateAction<ModalProps<IYard>>>;
}

const YardsModal = ({
	data,
	setModalProps,
	onClose,
	mode,
	isModalVisible,
	harbor,
	...props
}: YardsModalProps) => {
	const { t } = useTranslation();

	const [error, setError] = useState<string>('');

	useEffect(() => {
		if (!error) return;
		NotificationToast.Warn(t(error));
		setError('');
	}, [error]);

	const onSubmit = () => {
		if (!validate()) return;
		if (mode === 'edit') {
			onEdit();
			return;
		}
		onCreate();
	};

	const validate = (): boolean => {
		if (!data.name) {
			setError('alert.error.mandatory');
			return false;
		}
		return true;
	};

	const onCreate = async () => {
		const response = await YardService.create(
			{ ...data, harbor },
			setError
		);
		if (!response) return;
		NotificationToast.Success(
			`${t('port_management.yard')} ${t('alert.success.create')}`
		);
		onClose(true);
	};

	const onEdit = async () => {
		const response = await YardService.edit({ ...data, harbor }, setError);
		if (!response) return;
		NotificationToast.Success(
			`${t('port_management.yard')} ${t('alert.success.update')}`
		);
		onClose(true);
	};

	const handleInput = (
		e: React.ChangeEvent<HTMLInputElement> | EditableChangeEvent
	) => {
		setModalProps((prev) => ({
			...prev,
			data: {
				...prev.data,
				[e.target.name]: e.target.value,
			},
		}));
	};

	const renderToggles = () => (
		<div className="grid">
			<Toggle
				name="isActive"
				value={data.isActive}
				label={t('status.active')}
				onChange={handleInput}
				disabled={mode === 'view'}
			/>
		</div>
	);

	const renderInputs = () => (
		<div className="grid gap-x-2 gap-y-3">
			<Input
				containerClassName="col-span-full"
				value={data.name}
				name="name"
				label={t('misc.name')}
				placeholder={t('misc.name')}
				onChange={handleInput}
				disabled={mode === 'view'}
				required
			/>
			<Input
				containerClassName="col-span-full"
				type="number"
				value={data.sockets || ''}
				name="sockets"
				label={t('misc.sockets')}
				placeholder={t('misc.sockets')}
				onChange={handleInput}
				disabled={mode === 'view'}
			/>
		</div>
	);

	return (
		<Modal<IYard>
			onClose={onClose}
			data={data}
			mode={mode}
			onConfirm={onSubmit}
			isModalVisible={isModalVisible}
			{...props}
		>
			{renderToggles()}
			{renderInputs()}
		</Modal>
	);
};

export default YardsModal;
